import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCMS } from '../../context/CMSContext';
import {
  LayoutDashboard, Package, Image, Palette, Settings,
  LogOut, Menu, X, ExternalLink, Star, Users, Tag, Newspaper, Layout, PanelBottom, Percent, Truck, Mail
} from 'lucide-react';

// Sidebar Navigation
const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/products', icon: Package, label: 'Products' },
    { path: '/admin/categories', icon: Tag, label: 'Categories' },
    { path: '/admin/banners', icon: Image, label: 'Banners & Ads' },
    { path: '/admin/press', icon: Newspaper, label: 'Press Coverage' },
    { path: '/admin/orders', icon: Package, label: 'Orders' },
    { path: '/admin/discounts', icon: Percent, label: 'Discounts' },
    { path: '/admin/delivery', icon: Truck, label: 'Delivery' },
    { path: '/admin/reviews', icon: Star, label: 'Reviews' },
    { path: '/admin/contact', icon: Mail, label: 'Contact' },
    { path: '/admin/pages', icon: Layout, label: 'Pages' },
    { path: '/admin/footer', icon: PanelBottom, label: 'Footer' },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/themes', icon: Palette, label: 'Themes' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' }
  ];
  
  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };
  
  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };
  
  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="sidebar-overlay"
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 40,
            display: 'none'
          }}
        />
      )}
      
      <aside 
        className={`admin-sidebar ${isOpen ? 'open' : ''}`}
        style={{
          width: 260,
          backgroundColor: 'var(--color-secondary)',
          color: 'white',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.3s ease'
        }}
      >
        {/* Logo */}
        <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="flex items-center justify-between">
            <Link to="/admin" style={{ fontSize: '1.1rem', fontWeight: 800, textTransform: 'uppercase' }}>
              Prime CMS
            </Link>
            <button 
              onClick={onClose}
              className="mobile-close"
              style={{ 
                display: 'none',
                background: 'none', 
                border: 'none', 
                color: 'white', 
                cursor: 'pointer' 
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Navigation */}
        <nav style={{ flex: 1, padding: '1rem 0', overflowY: 'auto' }}>
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem 1.5rem',
                color: isActive(item.path, item.exact) ? 'var(--color-primary)' : 'rgba(255,255,255,0.7)',
                backgroundColor: isActive(item.path, item.exact) ? 'rgba(138,180,160,0.1)' : 'transparent',
                borderLeft: isActive(item.path, item.exact) ? '3px solid var(--color-primary)' : '3px solid transparent',
                transition: 'all 0.2s',
                fontSize: '0.9rem',
                fontWeight: 500
              }}
            >
              <item.icon size={20} />
              {item.label}
            </Link>
          ))}
        </nav>
        
        {/* View Site Link */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <a 
            href="/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2"
            style={{ 
              color: 'rgba(255,255,255,0.7)', 
              fontSize: '0.85rem',
              padding: '0.5rem 0'
            }}
          >
            <ExternalLink size={16} />
            View Site
          </a>
        </div>
        
        {/* User */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="flex items-center gap-3" style={{ marginBottom: '1rem' }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'var(--color-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-black)',
              fontWeight: 700
            }}>
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{user?.name || 'Admin'}</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>{user?.email}</div>
            </div>
          </div>
          <button 
            onClick={handleLogout}
            className="flex items-center gap-2"
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: 8,
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 500
            }}
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>
      
      <style>{`
        @media (max-width: 1024px) {
          .admin-sidebar {
            transform: translateX(-100%);
          }
          .admin-sidebar.open {
            transform: translateX(0);
          }
          .sidebar-overlay {
            display: block !important;
          }
          .mobile-close {
            display: block !important;
          }
        }
      `}</style>
    </>
  );
};

// Top Header
const AdminHeader = ({ onMenuClick }) => {
  const { products } = useCMS();
  const location = useLocation();
  
  // Get page title based on route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/admin') return 'Dashboard';
    if (path.includes('/products')) return 'Products';
    if (path.includes('/categories')) return 'Categories';
    if (path.includes('/banners')) return 'Banners & Ads';
    if (/\/orders\/[^/]+$/.test(path)) return 'Order';
    if (path.includes('/orders')) return 'Orders';
    if (path.includes('/discounts')) return 'Discounts';
    if (path.includes('/delivery')) return 'Delivery';
    if (path.includes('/reviews')) return 'Reviews';
    if (path.includes('/contact')) return 'Contact';
    if (path.includes('/pages')) return 'Pages';
    if (path.includes('/footer')) return 'Footer';
    if (path.includes('/users')) return 'Users';
    if (path.includes('/themes')) return 'Themes';
    if (path.includes('/settings')) return 'Settings';
    return 'Admin';
  };
  
  return (
    <header style={{
      height: 70,
      backgroundColor: 'var(--color-white)',
      borderBottom: '1px solid var(--color-gray-200)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 1.5rem',
      position: 'sticky',
      top: 0,
      zIndex: 30
    }}>
      <div className="flex items-center gap-4">
        <button 
          onClick={onMenuClick}
          className="mobile-menu-btn"
          style={{ 
            display: 'none',
            background: 'none', 
            border: 'none', 
            cursor: 'pointer',
            padding: '0.5rem'
          }}
        >
          <Menu size={24} />
        </button>
        <div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{getPageTitle()}</h1>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        {/* Quick Stats */}
        <div className="flex items-center gap-6" style={{ fontSize: '0.85rem', color: 'var(--color-gray-600)' }}>
          <span><strong>{products.length}</strong> Products</span>
        </div>
        
      </div>
      
      <style>{`
        @media (max-width: 1024px) {
          .mobile-menu-btn {
            display: block !important;
          }
        }
      `}</style>
    </header>
  );
};

// Main Admin Layout
const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  return (
    <div className="admin-layout" style={{ minHeight: '100vh', backgroundColor: 'var(--color-gray-50)' }}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="admin-main" style={{ marginLeft: 260, minHeight: '100vh' }}>
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} />
        
        <main style={{ padding: '1.5rem' }}>
          <Outlet />
        </main>
      </div>
      
      <style>{`
        @media (max-width: 1024px) {
          .admin-main {
            margin-left: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminLayout;
